import noop from 'lodash/noop'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { addToCart, AddToCartResponse, deleteCartItem, getCartItems } from '../common/requests'
import CartItem from '../dtos/CartItem'
import { ApplicationFlowContext } from './ApplicationFlowContextProvider'

type CartContext = {
  isAddedToCart: (subscriberId: number, planId: number) => boolean
  deleteItem: (subscriberId: number, planId: number) => void
  deleteItemById: (itemId: number) => void
  addItem: (subscriberId: number, planId: number, successMessage?: string) => Promise<AddToCartResponse>
}
export const CartContext = React.createContext<CartContext>({
  isAddedToCart: () => false,
  deleteItem: noop,
  deleteItemById: noop,
  addItem: () => Promise.resolve({ status: 'OK' })
});

interface CartContextProviderProps extends RouteComponentProps {
  children: JSX.Element
}

const CartContextProvider: React.FC<CartContextProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { setIsLoading, displayErrorMessage, organizationId, displaySuccessMessage } = useContext(ApplicationFlowContext);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    const initializeCartItems = async () => {
      setIsLoading(true);
      const cart = await getCartItems({ onError: (error?: string) => displayErrorMessage(!!error ? error : t('genericError')), organizationId: organizationId || undefined })
      setCartItems(cart.items);
      setIsLoading(false);
    };
    initializeCartItems();
  }, [displayErrorMessage, organizationId, setIsLoading, t]);

  const deleteItemById = useCallback(async (itemId: number) => {
    setIsLoading(true);
    await deleteCartItem(itemId, { onError: (error?: string) => displayErrorMessage(!!error ? error : t('genericError')) });
    const cart = await getCartItems({ onError: (error?: string) => displayErrorMessage(!!error ? error : t('genericError')) })
    setCartItems(cart.items);
    setIsLoading(false);
  }, [displayErrorMessage, setIsLoading, t]);

  const deleteItem = useCallback(async (subscriberId: number, planId: number) => {
    const item = cartItems.find(x => x.plan.id === planId && x.subscriber.id === subscriberId)
    if (item !== undefined) {
      await deleteItemById(item.id)
    }
  }, [cartItems, deleteItemById]);

  const addItem = useCallback(
    async (subscriberId: number, planId: number, successMessage?: string) => {
      setIsLoading(true);
      const response = await addToCart(
        subscriberId,
        planId,
        {
          onSuccess: () => displaySuccessMessage(t(successMessage || 'offers.successfullyAdded')),
          onError: (error?: string) => displayErrorMessage(!!error ? error : t('genericError'))
        }
      )
      const cart = await getCartItems({ onError: (error?: string) => displayErrorMessage(!!error ? error : t('genericError')) })
      setCartItems(cart.items);
      setIsLoading(false);
      return response
    },
    [displaySuccessMessage, displayErrorMessage, setIsLoading],
  )

  const isAddedToCart = useCallback(
    (subscriberId: number, planId: number) => {
      return cartItems.some(x => x.subscriber.id === subscriberId && x.plan.id === planId)
    },
    [cartItems],
  )

  return (
    <CartContext.Provider value={
      {
        isAddedToCart,
        deleteItemById,
        deleteItem,
        addItem
      }
    }>
      {children}
    </CartContext.Provider>
  )
};

export default withRouter(CartContextProvider);
